const API_BASE = import.meta.env.VITE_API_URL;

// Helper to check if response is 401 and handle logout
const handle401 = async (response) => {
  if (response.status === 401) {
    // Import dynamically to avoid circular dependency
    const { default: useAuthStore } = await import("../../store/authStore");
    useAuthStore.getState().logout();
    // Redirect to login
    window.location.href = "/#/login";
  }
};

export const getOrganizations = async (token) => {
  try {
    const response = await fetch(`${API_BASE}/api/organizations`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to fetch organizations");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error;
  }
};

export const updateOrganization = async (organizationId, updateData, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      },
    );

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to update organization");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};

export const createOrganization = async (organizationData, token) => {
  try {
    const response = await fetch(`${API_BASE}/api/organizations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(organizationData),
    });

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to create organization");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

export const deleteOrganization = async (organizationId, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to delete organization");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting organization:", error);
    throw error;
  }
};

export const getOrganizationMembers = async (organizationId, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/members`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    // If 404, return empty array (org doesn't exist or user isn't a member)
    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to fetch organization members");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching organization members:", error);
    throw error;
  }
};

export const updateOrganizationMemberRole = async (
  organizationId,
  memberId,
  role,
  token,
) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/members/${memberId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role }),
      },
    );

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to update member role");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating member role:", error);
    throw error;
  }
};

export const removeOrganizationMember = async (
  organizationId,
  memberId,
  token,
) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/members/${memberId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      throw new Error("Failed to remove member");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error removing member:", error);
    throw error;
  }
};

export const sendOrganizationInvite = async (
  organizationId,
  email,
  note,
  token,
) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/invite`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, note }),
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send invite");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending organization invite:", error);
    throw error;
  }
};

export const validateInviteToken = async (token) => {
  const response = await fetch(
    `${API_BASE}/api/organizations/validate-invite/${token}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to validate invite");
  }

  return await response.json();
};

export const acceptOrganizationInvite = async (token, acceptData) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/accept-invite/${token}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(acceptData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to accept invite");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error accepting organization invite:", error);
    throw error;
  }
};

// Create enterprise (invoice-based) organization
export const createEnterpriseOrganization = async (organizationData, token) => {
  try {
    const response = await fetch(`${API_BASE}/api/organizations/enterprise`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(organizationData),
    });

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to create enterprise organization",
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating enterprise organization:", error);
    throw error;
  }
};

// Send bulk invitations
export const sendBulkInvites = async (organizationId, emails, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/bulk-invite`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emails }),
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send bulk invites");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending bulk invites:", error);
    throw error;
  }
};

// Get invoice information for an organization
export const getInvoiceInfo = async (organizationId, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/invoice-info`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch invoice info");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching invoice info:", error);
    throw error;
  }
};

// Update invoice status (superadmin only)
export const updateInvoiceStatus = async (
  organizationId,
  statusData,
  token,
) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/invoice-status`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(statusData),
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update invoice status");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
};

// Get all invoice-based organizations (superadmin only)
export const getInvoiceOrganizations = async (token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/admin/invoice-orgs`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to fetch invoice organizations",
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching invoice organizations:", error);
    throw error;
  }
};

// Generate invoice for a specific organization
export const generateInvoice = async (organizationId, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/generate-invoice`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to generate invoice");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error generating invoice:", error);
    throw error;
  }
};

// Get invoice history for an organization
export const getOrganizationInvoices = async (organizationId, token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/${organizationId}/invoices`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch invoices");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

// Generate monthly invoices for all invoice organizations (superadmin only)
export const generateAllMonthlyInvoices = async (token) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/organizations/admin/generate-monthly-invoices`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      await handle401(response);
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to generate monthly invoices",
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error generating monthly invoices:", error);
    throw error;
  }
};
